import { useState } from "react";
import { BREAKDOWN_MODES, expandRow } from "../utils/doublesAnalysis";

const pctOf = (won, total) => (total > 0 ? `${Math.round((won * 100) / total)}%` : "\u2014");

// Small "Combined / By Player / 1st-2nd / Player + 1st-2nd" segmented control,
// shared by every breakdown-capable Doubles Analysis table (Net Position is
// the one category excluded — see doublesAnalysis.js's BREAKDOWN_MODES).
function BreakdownSelector({ value, onChange }) {
  return (
    <div className="join">
      {BREAKDOWN_MODES.map((m) => (
        <button
          key={m.id}
          className={`join-item btn btn-xs ${value === m.id ? "btn-active" : "btn-base-100"}`}
          onClick={() => onChange(m.id)}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

// Pair up focus/opposition sub-rows produced by expandRow() so they render on
// the same table row — matched by label (player name / "1st Serve" / etc.),
// falling back to a blank opponent cell when only one side has that label.
// Exported for reuse by AIReports.jsx's PDF export (same pairing needed there).
export function mergeExpandedRows(focusExpanded, oppExpanded) {
  const map = new Map();
  for (const r of focusExpanded) map.set(r.label, { label: r.label, focus: r, opp: null });
  for (const r of oppExpanded ?? []) {
    const existing = map.get(r.label);
    if (existing) existing.opp = r;
    else map.set(r.label, { label: r.label, focus: null, opp: r });
  }
  return [...map.values()];
}

// A formation/direction/hand row is worth showing if either side actually
// used it at least once — filtering on the focus side alone could hide a
// formation the opposition used but the focus team never did.
function renderFormationTable(title, focusRows, oppRows, mode, onModeChange) {
  const rows = focusRows
    .map((row, i) => ({ row, oppRow: oppRows?.[i] }))
    .filter(({ row, oppRow }) => row.total.points > 0 || (oppRow && oppRow.total.points > 0));
  if (rows.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
        <p className="text-xs font-semibold">{title}</p>
        <BreakdownSelector value={mode} onChange={onModeChange} />
      </div>
      <table className="table table-xs w-full">
        <thead>
          <tr>
            <th className="text-left">Formation</th>
            <th className="text-right">Points</th>
            <th className="text-right">Won</th>
            {oppRows && <th className="text-right text-base-content/50">Opponent</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ row, oppRow }) =>
            mode === "combined" ? (
              <tr key={row.index}>
                <td>{row.label}</td>
                <td className="text-right">{row.total.points}</td>
                <td className="text-right">{row.total.won}/{row.total.points} ({pctOf(row.total.won, row.total.points)})</td>
                {oppRows && (
                  <td className="text-right text-base-content/50">
                    {oppRow && oppRow.total.points > 0 ? `${oppRow.total.won}/${oppRow.total.points} (${pctOf(oppRow.total.won, oppRow.total.points)})` : "\u2014"}
                  </td>
                )}
              </tr>
            ) : (
              <FormationGroup key={row.index} label={row.label} sub={mergeExpandedRows(expandRow(row, mode), oppRow ? expandRow(oppRow, mode) : null)} hasOpp={!!oppRows} />
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}

// Renders a formation's group header + its player/serve# sub-rows.
function FormationGroup({ label, sub, hasOpp }) {
  return (
    <>
      <tr className="bg-base-200/60">
        <td colSpan={hasOpp ? 4 : 3} className="text-xs font-semibold">{label}</td>
      </tr>
      {sub.map((s) => (
        <tr key={s.label}>
          <td className="pl-4 text-base-content/80">{s.label}</td>
          <td className="text-right">{s.focus?.points ?? 0}</td>
          <td className="text-right">
            {s.focus?.points ? `${s.focus.won}/${s.focus.points} (${pctOf(s.focus.won, s.focus.points)})` : "\u2014"}
          </td>
          {hasOpp && (
            <td className="text-right text-base-content/50">
              {s.opp?.points ? `${s.opp.won}/${s.opp.points} (${pctOf(s.opp.won, s.opp.points)})` : "\u2014"}
            </td>
          )}
        </tr>
      ))}
    </>
  );
}

function renderServeDirectionTable(focusRows, oppRows, mode, onModeChange) {
  const rows = focusRows
    .map((row, i) => ({ row, oppRow: oppRows?.[i] }))
    .filter(({ row, oppRow }) => row.total.points > 0 || (oppRow && oppRow.total.points > 0));
  if (rows.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
        <p className="text-xs font-semibold">Serve Direction</p>
        <BreakdownSelector value={mode} onChange={onModeChange} />
      </div>
      <table className="table table-xs w-full">
        <thead>
          <tr>
            <th className="text-left">Zone</th>
            <th className="text-right">Attempts</th>
            <th className="text-right">In%</th>
            <th className="text-right">Won%</th>
            {oppRows && <th className="text-right text-base-content/50">Opponent</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ row, oppRow }) =>
            mode === "combined" ? (
              <tr key={row.index}>
                <td>{row.label}</td>
                <td className="text-right">{row.total.points}</td>
                <td className="text-right">{pctOf(row.total.points - row.total.faults, row.total.points)}</td>
                <td className="text-right">{pctOf(row.total.won, row.total.points - row.total.faults)}</td>
                {oppRows && (
                  <td className="text-right text-base-content/50">
                    {oppRow && oppRow.total.points > 0
                      ? `In ${pctOf(oppRow.total.points - oppRow.total.faults, oppRow.total.points)} \u00b7 Won ${pctOf(oppRow.total.won, oppRow.total.points - oppRow.total.faults)}`
                      : "\u2014"}
                  </td>
                )}
              </tr>
            ) : (
              <ServeDirectionGroup key={row.index} label={row.label} sub={mergeExpandedRows(expandRow(row, mode), oppRow ? expandRow(oppRow, mode) : null)} hasOpp={!!oppRows} />
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}

function ServeDirectionGroup({ label, sub, hasOpp }) {
  return (
    <>
      <tr className="bg-base-200/60">
        <td colSpan={hasOpp ? 5 : 4} className="text-xs font-semibold">{label}</td>
      </tr>
      {sub.map((s) => {
        const fIn = s.focus ? s.focus.points - s.focus.faults : 0;
        const oIn = s.opp ? s.opp.points - s.opp.faults : 0;
        return (
          <tr key={s.label}>
            <td className="pl-4 text-base-content/80">{s.label}</td>
            <td className="text-right">{s.focus?.points ?? 0}</td>
            <td className="text-right">{s.focus?.points ? pctOf(fIn, s.focus.points) : "\u2014"}</td>
            <td className="text-right">{s.focus?.points ? pctOf(s.focus.won, fIn) : "\u2014"}</td>
            {hasOpp && (
              <td className="text-right text-base-content/50">
                {s.opp?.points ? `In ${pctOf(oIn, s.opp.points)} \u00b7 Won ${pctOf(s.opp.won, oIn)}` : "\u2014"}
              </td>
            )}
          </tr>
        );
      })}
    </>
  );
}

// Net Position has no breakdown (see doublesAnalysis.js's BREAKDOWN_MODES
// comment) — kept as the original flat, non-expandable table.
function renderNetPositionTable(focusRows, oppRows) {
  const rows = focusRows
    .map((row, i) => ({ row, oppRow: oppRows?.[i] }))
    .filter(({ row, oppRow }) => row.points > 0 || (oppRow && oppRow.points > 0));
  if (rows.length === 0) return null;
  return (
    <div className="mb-4">
      <p className="text-xs font-semibold mb-1">Net Position (on serve)</p>
      <table className="table table-xs w-full">
        <thead>
          <tr>
            <th className="text-left">Formation</th>
            <th className="text-right">Points</th>
            <th className="text-right">Won</th>
            {oppRows && <th className="text-right text-base-content/50">Opponent</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ row, oppRow }) => (
            <tr key={row.index}>
              <td>{row.label}</td>
              <td className="text-right">{row.points}</td>
              <td className="text-right">{row.won}/{row.points} ({pctOf(row.won, row.points)})</td>
              {oppRows && (
                <td className="text-right text-base-content/50">
                  {oppRow && oppRow.points > 0 ? `${oppRow.won}/${oppRow.points} (${pctOf(oppRow.won, oppRow.points)})` : "\u2014"}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderReturnHandTable(focusHand, oppHand, mode, onModeChange) {
  const rows = [
    { row: focusHand.forehand, oppRow: oppHand?.forehand },
    { row: focusHand.backhand, oppRow: oppHand?.backhand },
  ].filter(({ row, oppRow }) => row.total.points > 0 || (oppRow && oppRow.total.points > 0));
  if (rows.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
        <p className="text-xs font-semibold">Return Success by Hand</p>
        <BreakdownSelector value={mode} onChange={onModeChange} />
      </div>
      <table className="table table-xs w-full">
        <thead>
          <tr>
            <th className="text-left">Hand</th>
            <th className="text-right">Returns</th>
            <th className="text-right">Won</th>
            {oppHand && <th className="text-right text-base-content/50">Opponent</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ row, oppRow }) =>
            mode === "combined" ? (
              <tr key={row.index}>
                <td>{row.label}</td>
                <td className="text-right">{row.total.points}</td>
                <td className="text-right">{row.total.won}/{row.total.points} ({pctOf(row.total.won, row.total.points)})</td>
                {oppHand && (
                  <td className="text-right text-base-content/50">
                    {oppRow && oppRow.total.points > 0 ? `${oppRow.total.won}/${oppRow.total.points} (${pctOf(oppRow.total.won, oppRow.total.points)})` : "\u2014"}
                  </td>
                )}
              </tr>
            ) : (
              <FormationGroup key={row.index} label={row.label} sub={mergeExpandedRows(expandRow(row, mode), oppRow ? expandRow(oppRow, mode) : null)} hasOpp={!!oppHand} />
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The 5 Doubles Analysis tables (Serve Formations, Serve Direction, Net
 * Position, Return Formations, Return Success by Hand), fully controlled —
 * caller owns the 4 breakdown-mode selections (Net Position has none). Used
 * directly by GeneralReport.jsx (which wraps it with its own header + AI
 * Report button) and indirectly by AIReports.jsx via `DoublesAnalysisSection`
 * below.
 */
export function DoublesAnalysisTables({
  analysis,
  serveFormationMode,
  onServeFormationModeChange,
  serveDirectionMode,
  onServeDirectionModeChange,
  returnFormationMode,
  onReturnFormationModeChange,
  returnHandMode,
  onReturnHandModeChange,
}) {
  if (!analysis) return null;
  return (
    <>
      {renderFormationTable("Serve Formations", analysis.focus.serveFormations, analysis.opposition?.serveFormations, serveFormationMode, onServeFormationModeChange)}
      {renderServeDirectionTable(analysis.focus.serveDirection, analysis.opposition?.serveDirection, serveDirectionMode, onServeDirectionModeChange)}
      {renderNetPositionTable(analysis.focus.netPositions, analysis.opposition?.netPositions)}
      {renderFormationTable("Return Formations", analysis.focus.returnFormations, analysis.opposition?.returnFormations, returnFormationMode, onReturnFormationModeChange)}
      {renderReturnHandTable(analysis.focus.returnHand, analysis.opposition?.returnHand, returnHandMode, onReturnHandModeChange)}
    </>
  );
}

/**
 * Self-contained "Doubles Analysis" section (heading + the 5 tables above),
 * owning its own breakdown-mode state — used by pages that don't need a
 * custom header (e.g. AIReports.jsx). `defaultMode` seeds all 4 breakdown
 * selectors at once (GeneralReport.jsx's own section defaults each to
 * "combined" via DoublesAnalysisTables directly; this convenience wrapper
 * lets a caller start pre-expanded, e.g. "playerServe" for the AI report).
 */
export function DoublesAnalysisSection({ analysis, defaultMode = "combined", title = "Doubles Analysis" }) {
  const [serveFormationMode, setServeFormationMode] = useState(defaultMode);
  const [serveDirectionMode, setServeDirectionMode] = useState(defaultMode);
  const [returnFormationMode, setReturnFormationMode] = useState(defaultMode);
  const [returnHandMode, setReturnHandMode] = useState(defaultMode);

  if (!analysis) return null;
  return (
    <section>
      <h2 className="text-sm font-bold border-b border-base-300 pb-1 mb-3 uppercase tracking-wide">{title}</h2>
      <DoublesAnalysisTables
        analysis={analysis}
        serveFormationMode={serveFormationMode}
        onServeFormationModeChange={setServeFormationMode}
        serveDirectionMode={serveDirectionMode}
        onServeDirectionModeChange={setServeDirectionMode}
        returnFormationMode={returnFormationMode}
        onReturnFormationModeChange={setReturnFormationMode}
        returnHandMode={returnHandMode}
        onReturnHandModeChange={setReturnHandMode}
      />
    </section>
  );
}
